import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Phone, Settings, Shield, Check, X, RefreshCw, Loader2, AlertTriangle,
  PhoneCall, PhoneIncoming, PhoneOutgoing, MessageSquare, Clock, User,
  Activity, Server, Key, Link2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VoIPSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('configuration');
  
  // Configuration
  const [config, setConfig] = useState({
    server_url: '',
    client_id: '',
    client_secret: '',
    is_enabled: false,
    default_extension: '100'
  });
  
  // Status
  const [status, setStatus] = useState(null);
  
  // Dashboard
  const [dashboard, setDashboard] = useState(null);
  
  // Call Logs
  const [callLogs, setCallLogs] = useState([]);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/voip/config`, { headers });
      setConfig(prev => ({
        ...prev,
        server_url: res.data.server_url || '',
        client_id: res.data.client_id || '',
        is_enabled: res.data.is_enabled || false,
        default_extension: res.data.default_extension || '100'
      }));
    } catch (err) {
      console.error('Failed to fetch VoIP config:', err);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/voip/status`, { headers });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch VoIP status:', err);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/voip/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch VoIP dashboard:', err);
    }
  }, []);

  const fetchCallLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/voip/calls?limit=50`, { headers });
      setCallLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch call logs:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchStatus(), fetchDashboard(), fetchCallLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchConfig, fetchStatus, fetchDashboard, fetchCallLogs]);

  const saveConfig = async () => {
    try {
      setSaving(true);
      const params = new URLSearchParams();
      if (config.server_url) params.append('server_url', config.server_url);
      if (config.client_id) params.append('client_id', config.client_id);
      if (config.client_secret) params.append('client_secret', config.client_secret);
      params.append('is_enabled', config.is_enabled);
      if (config.default_extension) params.append('default_extension', config.default_extension);

      await axios.put(`${API}/api/voip/config?${params}`, null, { headers });
      toast.success('VoIP configuration saved');
      fetchStatus();
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    toast.info('Testing 3CX connection...');
    // In production, this would actually test the 3CX connection
    await fetchStatus();
    if (config.server_url && config.client_id) {
      toast.success('Connection test passed (Demo mode)');
    } else {
      toast.warning('Please configure server URL and credentials first');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voip-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Phone className="h-6 w-6" />
            VoIP Settings
          </h1>
          <p className="text-muted-foreground">Configure 3CX integration for calls and SMS</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={status?.is_enabled ? 'default' : 'secondary'} className="text-sm">
            {status?.is_enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Badge 
            variant={status?.is_configured ? 'default' : 'destructive'} 
            className={`text-sm ${status?.is_configured ? 'bg-green-500' : ''}`}
          >
            {status?.is_configured ? 'Connected' : 'Not Configured'}
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="call_logs">Call Logs</TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* 3CX Connection Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  3CX Server Connection
                </CardTitle>
                <CardDescription>
                  Enter your 3CX server credentials to enable click-to-call and SMS features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Server URL</Label>
                  <div className="relative">
                    <Link2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="https://your-company.3cx.com"
                      value={config.server_url}
                      onChange={(e) => setConfig(prev => ({ ...prev, server_url: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Your 3CX WebClient URL</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Enter your 3CX Client ID"
                      value={config.client_id}
                      onChange={(e) => setConfig(prev => ({ ...prev, client_id: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Client Secret</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="Enter your 3CX Client Secret"
                      value={config.client_secret}
                      onChange={(e) => setConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">This will be encrypted and stored securely</p>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button variant="outline" onClick={testConnection}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Test Connection
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* General Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  General Settings
                </CardTitle>
                <CardDescription>
                  Configure default behavior for VoIP features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable VoIP Features</Label>
                    <p className="text-xs text-muted-foreground">Turn on click-to-call and SMS functionality</p>
                  </div>
                  <Switch
                    checked={config.is_enabled}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, is_enabled: checked }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Default Extension</Label>
                  <Input
                    placeholder="100"
                    value={config.default_extension}
                    onChange={(e) => setConfig(prev => ({ ...prev, default_extension: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Default extension for outbound calls</p>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">3CX Integration</p>
                      <p className="text-sm text-blue-700 mt-1">
                        To enable 3CX integration, you need:
                      </p>
                      <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
                        <li>3CX Phone System v18 or higher</li>
                        <li>API credentials from 3CX admin console</li>
                        <li>WebClient URL for your 3CX instance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => fetchConfig()}>
              Cancel
            </Button>
            <Button onClick={saveConfig} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save Configuration
            </Button>
          </div>
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Today's Calls</p>
                    <p className="text-3xl font-bold">{dashboard?.today?.calls || 0}</p>
                  </div>
                  <PhoneCall className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Today's SMS</p>
                    <p className="text-3xl font-bold">{dashboard?.today?.sms || 0}</p>
                  </div>
                  <MessageSquare className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-3xl font-bold">{dashboard?.total?.calls || 0}</p>
                  </div>
                  <Phone className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Duration</p>
                    <p className="text-3xl font-bold">
                      {formatDuration(Math.round(dashboard?.duration_stats?.avg_duration || 0))}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneCall className="h-5 w-5" />
                  Recent Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {(dashboard?.recent_calls || []).map((call) => (
                      <div key={call.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          {call.direction === 'outbound' ? (
                            <PhoneOutgoing className="h-4 w-4 text-blue-500" />
                          ) : (
                            <PhoneIncoming className="h-4 w-4 text-green-500" />
                          )}
                          <div>
                            <p className="font-medium">{call.contact_name}</p>
                            <p className="text-xs text-muted-foreground">{call.contact_phone}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={call.status === 'ended' ? 'default' : 'secondary'}>
                            {call.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(call.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {(!dashboard?.recent_calls || dashboard.recent_calls.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No recent calls</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Recent SMS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {(dashboard?.recent_sms || []).map((sms) => (
                      <div key={sms.id} className="p-3 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{sms.contact_name}</span>
                          <span className="text-xs text-muted-foreground">{formatDate(sms.created_at)}</span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{sms.message}</p>
                      </div>
                    ))}
                    {(!dashboard?.recent_sms || dashboard.recent_sms.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No recent SMS</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Call Logs Tab */}
        <TabsContent value="call_logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Call History</CardTitle>
              <CardDescription>Complete log of all calls made through the system</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {callLogs.map((call) => (
                    <div key={call.id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${call.direction === 'outbound' ? 'bg-blue-100' : 'bg-green-100'}`}>
                          {call.direction === 'outbound' ? (
                            <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                          ) : (
                            <PhoneIncoming className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{call.contact_name}</p>
                          <p className="text-sm text-muted-foreground">{call.contact_phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm">{formatDuration(call.duration_seconds || 0)}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(call.created_at)}</p>
                        </div>
                        <Badge variant={call.status === 'ended' ? 'default' : call.status === 'missed' ? 'destructive' : 'secondary'}>
                          {call.status}
                        </Badge>
                        {call.agent_name && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <User className="h-3 w-3" />
                            {call.agent_name}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {callLogs.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No call logs found</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VoIPSettingsPage;
